/**
 * Calculate heatmap data from climbing ascents
 * Groups ascents by grade and time period, tracking different ascent types
 */

import { calculateCGP } from './cgp.js';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Get time period key from a date string
 * @param {string} dateStr - Date string (e.g., "2024-03-15")
 * @param {string} period - 'monthly', 'quarterly', or 'yearly'
 * @returns {{ year: number, key: string, label: string }}
 */
function getTimePeriod(dateStr, period = 'quarterly') {
  const date = new Date(dateStr);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth(); // 0-11

  switch (period) {
    case 'monthly':
      return {
        year,
        key: `${year}-${String(month + 1).padStart(2, '0')}`,
        label: MONTH_NAMES[month]
      };
    case 'yearly':
      return {
        year,
        key: `${year}`,
        label: `${year}`
      };
    case 'half-yearly':
      const half = month < 6 ? 1 : 2;
      return {
        year,
        key: `${year}-H${half}`,
        label: `H${half}`
      };
    case 'quarterly':
    default:
      const quarter = Math.floor(month / 3) + 1;
      return {
        year,
        key: `${year}-Q${quarter}`,
        label: `Q${quarter}`
      };
  }
}

/**
 * Calculate heatmap data from processed logbook data
 * @param {Array} data - Processed logbook data array
 * @param {string} climbingStyle - 'sport', 'trad', or 'boulder'
 * @param {string} timePeriod - 'monthly', 'quarterly', or 'yearly'
 * @returns {Object} Heatmap data structure
 */
export function calculateHeatmap(data, climbingStyle = 'sport', timePeriod = 'quarterly') {
  if (!data?.length) {
    return {
      periods: [],
      periodLabels: {},
      grades: [],
      heatmapClean: {},
      heatmapFlash: {},
      heatmapAttempts: {},
      routesClean: {},
      routesFlash: {},
      routesAttempts: {},
      flashBreakdown: {},
      cgpByPeriod: {},
      cgpPerDayByPeriod: {}
    };
  }

  // Filter data based on climbing style
  let filteredData = data;
  if (climbingStyle === 'sport') {
    filteredData = data.filter(d =>
      d['Route Gear Style'] !== 'Boulder' &&
      d['Route Gear Style'] !== 'Trad' &&
      d['Route Gear Style'] !== 'Ice' &&
      d['Route Gear Style'] !== 'Alpine'
    );
  } else if (climbingStyle === 'trad') {
    filteredData = data.filter(d => d['Route Gear Style'] === 'Trad');
  } else if (climbingStyle === 'boulder') {
    filteredData = data.filter(d => d['Route Gear Style'] === 'Boulder');
  }

  if (!filteredData.length) {
    return {
      periods: [],
      periodLabels: {},
      grades: [],
      heatmapClean: {},
      heatmapFlash: {},
      heatmapAttempts: {},
      routesClean: {},
      routesFlash: {},
      routesAttempts: {},
      flashBreakdown: {},
      cgpByPeriod: {},
      cgpPerDayByPeriod: {}
    };
  }

  // Find date range
  const dates = filteredData.map(a => new Date(a['Ascent Date'])).filter(d => !isNaN(d));
  if (dates.length === 0) {
    return {
      periods: [],
      periodLabels: {},
      grades: [],
      heatmapClean: {},
      heatmapFlash: {},
      heatmapAttempts: {},
      routesClean: {},
      routesFlash: {},
      routesAttempts: {},
      flashBreakdown: {},
      cgpByPeriod: {},
      cgpPerDayByPeriod: {}
    };
  }

  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));
  const minYear = minDate.getUTCFullYear();
  const maxYear = maxDate.getUTCFullYear();

  // Generate all periods for complete years (if any period of a year has data, show all periods)
  const allPeriods = [];
  const periodLabels = {};

  if (timePeriod === 'yearly') {
    for (let year = minYear; year <= maxYear; year++) {
      const key = `${year}`;
      allPeriods.push(key);
      periodLabels[key] = `${year}`;
    }
  } else if (timePeriod === 'half-yearly') {
    for (let year = minYear; year <= maxYear; year++) {
      for (let half = 1; half <= 2; half++) {
        const key = `${year}-H${half}`;
        allPeriods.push(key);
        periodLabels[key] = `H${half}`;
      }
    }
  } else if (timePeriod === 'quarterly') {
    for (let year = minYear; year <= maxYear; year++) {
      for (let quarter = 1; quarter <= 4; quarter++) {
        const key = `${year}-Q${quarter}`;
        allPeriods.push(key);
        periodLabels[key] = `Q${quarter}`;
      }
    }
  } else {
    // monthly
    for (let year = minYear; year <= maxYear; year++) {
      for (let month = 0; month < 12; month++) {
        const key = `${year}-${String(month + 1).padStart(2, '0')}`;
        allPeriods.push(key);
        periodLabels[key] = MONTH_NAMES[month];
      }
    }
  }

  const sortedPeriods = allPeriods;

  // Get all grades present in the data
  const allGrades = new Set();
  const gradeField = climbingStyle === 'boulder' ? 'vgrade' : 'ewbanks';

  filteredData.forEach(a => {
    const grade = Math.round(a[gradeField]);
    if (climbingStyle === 'boulder') {
      if (grade >= 0 && grade <= 15) allGrades.add(grade);
    } else {
      if (grade >= 10 && grade <= 35) allGrades.add(grade);
    }
  });

  const grades = [...allGrades].sort((a, b) => b - a); // High to low

  // Initialize heatmap structures
  const heatmapClean = {};
  const heatmapFlash = {};
  const heatmapAttempts = {};
  const routesClean = {};
  const routesFlash = {};
  const routesAttempts = {};
  // Track onsight vs flash breakdown for color blending
  const flashBreakdown = {};

  grades.forEach(g => {
    heatmapClean[g] = {};
    heatmapFlash[g] = {};
    heatmapAttempts[g] = {};
    routesClean[g] = {};
    routesFlash[g] = {};
    routesAttempts[g] = {};
    flashBreakdown[g] = {};

    sortedPeriods.forEach(p => {
      heatmapClean[g][p] = 0;
      heatmapFlash[g][p] = 0;
      heatmapAttempts[g][p] = 0;
      routesClean[g][p] = [];
      routesFlash[g][p] = [];
      routesAttempts[g][p] = [];
      flashBreakdown[g][p] = { onsight: 0, flash: 0 };
    });
  });

  // Populate heatmaps
  filteredData.forEach(a => {
    const grade = Math.round(a[gradeField]);
    const { key } = getTimePeriod(a['Ascent Date'], timePeriod);

    if (heatmapClean[grade] && heatmapClean[grade][key] !== undefined) {
      const routeInfo = {
        name: a['Route Name'],
        crag: a['Crag Name'],
        date: a['Ascent Date'].split('T')[0],
        type: a['Ascent Type'],
        grade: a['Route Grade']
      };

      // All attempts
      heatmapAttempts[grade][key]++;
      routesAttempts[grade][key].push(routeInfo);

      // Clean sends
      if (a.isClean) {
        heatmapClean[grade][key]++;
        routesClean[grade][key].push(routeInfo);
      }

      // Flash/Onsight only (or Send for bouldering)
      const ascentType = a['Ascent Type'];
      const flashTypes = climbingStyle === 'boulder'
        ? ['Onsight', 'Flash', 'Send']
        : ['Onsight', 'Flash'];

      if (flashTypes.includes(ascentType)) {
        heatmapFlash[grade][key]++;
        routesFlash[grade][key].push(routeInfo);

        // Track onsight vs flash for color blending
        if (ascentType === 'Onsight') {
          flashBreakdown[grade][key].onsight++;
        } else {
          flashBreakdown[grade][key].flash++;
        }
      }
    }
  });

  // Compute CGP per period (uses clean-ascent filter internally)
  const ascentsByPeriod = {};
  sortedPeriods.forEach(p => { ascentsByPeriod[p] = []; });
  filteredData.forEach(a => {
    const { key } = getTimePeriod(a['Ascent Date'], timePeriod);
    if (ascentsByPeriod[key]) ascentsByPeriod[key].push(a);
  });

  const cgpByPeriod = {};
  const cgpPerDayByPeriod = {};
  sortedPeriods.forEach(p => {
    const ascents = ascentsByPeriod[p];
    const cgp = ascents.length ? calculateCGP(ascents, climbingStyle) : 0;
    cgpByPeriod[p] = cgp;

    const uniqueDays = new Set(ascents.map(a => a['Ascent Date'].split('T')[0])).size;
    cgpPerDayByPeriod[p] = (cgp && uniqueDays > 0) ? Math.log2(Math.pow(2, cgp) / uniqueDays) : 0;
  });

  return {
    periods: sortedPeriods,
    periodLabels,
    grades,
    heatmapClean,
    heatmapFlash,
    heatmapAttempts,
    routesClean,
    routesFlash,
    routesAttempts,
    flashBreakdown,
    cgpByPeriod,
    cgpPerDayByPeriod
  };
}

export default calculateHeatmap;
